import React, { useContext, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { Icon24 } from '../Icon'
import {
  Spinner,
  Wrapper,
  InfoContainer,
  Container,
  Text,
  DismissButton,
  IconWrapper,
  SuccessIconWrapper,
} from './styles'
import { SnackBarContext } from './SnackBarContext'
import { SnackBarTypes } from './types'

const SnackBarIcon: React.FC<{ type: SnackBarTypes }> = ({ type }) => {
  const iconMap: Record<SnackBarTypes, React.ReactNode> = {
    [SnackBarTypes.success]: (
      <SuccessIconWrapper>
        <Icon24.CircleSuccess />
      </SuccessIconWrapper>
    ),
    [SnackBarTypes.failure]: <Icon24.CircleError />,
    [SnackBarTypes.loading]: <Spinner />,
    [SnackBarTypes.simple]: null,
  }

  return <IconWrapper>{iconMap[type]}</IconWrapper>
}

export const SnackBar: React.FC = () => {
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()
  const { isOpen, text, type, isDismissible, action, bottom, closeSnackBar, clearSnackBarState } =
    useContext(SnackBarContext)
  const safeType = type ?? SnackBarTypes.simple
  const transitionRef = useRef(null)

  return (
    <Transition
      in={isOpen}
      onExited={clearSnackBarState}
      timeout={{ enter: 300, exit: 300 }}
      mountOnEnter
      unmountOnExit
      nodeRef={transitionRef}
    >
      {state => (
        <Wrapper
          data-testid="snack-bar"
          $transitionState={state}
          $bottom={bottom}
          $isPhone={isPhone}
          ref={transitionRef}
        >
          <Container>
            <InfoContainer>
              {type !== SnackBarTypes.simple && <SnackBarIcon type={safeType} />}
              <Text>{text}</Text>
            </InfoContainer>
            {isDismissible && (
              <DismissButton onClick={closeSnackBar}>
                {i18n.t('builder.resume_editor.dismiss')}
              </DismissButton>
            )}
            {action?.onClick && action.text && !isDismissible && (
              <DismissButton onClick={action.onClick}>{action.text}</DismissButton>
            )}
          </Container>
        </Wrapper>
      )}
    </Transition>
  )
}
